import React from 'react';

const QUESTION_TYPES = [
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'true_false', label: 'True / False' },
  { value: 'short_text', label: 'Short Text Answer' },
  { value: 'long_answer', label: 'Long Answer / Descriptive' },
  { value: 'fill_blank', label: 'Fill in the Blank' },
  { value: 'numerical', label: 'Numerical Answer' },
];

// Shared per-question-type editor, reused by both the create and edit flow
// on the Questions page. `formData`/`setFormData` hold the whole question
// object; this component only knows how to render/mutate the fields
// relevant to the currently selected question_type.
function MockExamQuestionForm({ formData, setFormData, disabled }) {
  const set = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  const options = formData.options || [];
  const setOptions = (newOptions) => set('options', newOptions);
  const updateOption = (idx, key, value) => {
    const next = options.map((o, i) => (i === idx ? { ...o, [key]: value } : o));
    setOptions(next);
  };
  const addOption = () => setOptions([...options, { text: '', is_correct: false }]);
  const removeOption = (idx) => setOptions(options.filter((_, i) => i !== idx));
  const setSingleCorrect = (idx) => setOptions(options.map((o, i) => ({ ...o, is_correct: i === idx })));
  const toggleMultiCorrect = (idx) => setOptions(options.map((o, i) => (i === idx ? { ...o, is_correct: !o.is_correct } : o)));

  const acceptedAnswers = formData.accepted_answers || [];
  const setAcceptedAnswers = (list) => set('accepted_answers', list);
  const addAcceptedAnswer = () => setAcceptedAnswers([...acceptedAnswers, '']);
  const updateAcceptedAnswer = (idx, value) => setAcceptedAnswers(acceptedAnswers.map((a, i) => (i === idx ? value : a)));
  const removeAcceptedAnswer = (idx) => setAcceptedAnswers(acceptedAnswers.filter((_, i) => i !== idx));

  const initTrueFalse = () => {
    if (!options.length) setOptions([{ text: 'True', is_correct: true }, { text: 'False', is_correct: false }]);
  };

  return (
    <div className="row g-20">
      <div className="col-sm-6">
        <label className="h5 mb-8 fw-semibold font-heading">Question Type <span className="text-red-500">*</span></label>
        <select
          className="form-control py-11"
          value={formData.question_type}
          onChange={(e) => {
            const type = e.target.value;
            set('question_type', type);
            if (type === 'true_false') {
              setTimeout(initTrueFalse, 0);
            } else if (['single_choice', 'multiple_choice'].includes(type) && (!formData.options || formData.options.length === 0)) {
              setOptions([{ text: '', is_correct: false }, { text: '', is_correct: false }]);
            }
          }}
          disabled={disabled}
        >
          {QUESTION_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
        </select>
      </div>
      <div className="col-sm-3">
        <label className="h5 mb-8 fw-semibold font-heading">Marks <span className="text-red-500">*</span></label>
        <input type="number" min="0" step="0.5" className="form-control py-11" value={formData.marks ?? 1}
          onChange={(e) => set('marks', e.target.value)} disabled={disabled} />
      </div>
      <div className="col-sm-3">
        <label className="h5 mb-8 fw-semibold font-heading">Negative Marks</label>
        <input type="number" min="0" step="0.25" className="form-control py-11" value={formData.negative_marks ?? 0}
          onChange={(e) => set('negative_marks', e.target.value)} disabled={disabled} />
      </div>

      <div className="col-12">
        <label className="h5 mb-8 fw-semibold font-heading d-block">Question Text <span className="text-red-500">*</span></label>
        <textarea className="form-control py-11" rows={3} value={formData.question_text || ''}
          onChange={(e) => set('question_text', e.target.value)} disabled={disabled} />
      </div>

      {['single_choice', 'multiple_choice', 'true_false'].includes(formData.question_type) && (
        <div className="col-12">
          <label className="h5 mb-8 fw-semibold font-heading d-block">
            Answer Options <span className="text-red-500">*</span>
            {formData.question_type === 'multiple_choice' ? ' (check all correct options)' : ' (select the one correct option)'}
          </label>
          {options.map((opt, idx) => (
            <div key={idx} className="d-flex align-items-center gap-8 mb-8">
              <input
                type={formData.question_type === 'multiple_choice' ? 'checkbox' : 'radio'}
                checked={!!opt.is_correct}
                onChange={() => (formData.question_type === 'multiple_choice' ? toggleMultiCorrect(idx) : setSingleCorrect(idx))}
                disabled={disabled}
                title="Correct answer"
              />
              <input
                type="text" className="form-control py-9" placeholder={`Option ${idx + 1}`}
                value={opt.text} onChange={(e) => updateOption(idx, 'text', e.target.value)}
                disabled={disabled || formData.question_type === 'true_false'}
              />
              {formData.question_type !== 'true_false' && options.length > 2 && (
                <button type="button" className="btn btn-sm py-4 px-8" style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 }} onClick={() => removeOption(idx)} disabled={disabled}>
                  <i className="ph ph-x" />
                </button>
              )}
            </div>
          ))}
          {formData.question_type !== 'true_false' && (
            <button type="button" className="btn btn-sm btn-outline-main py-6 px-12 mt-4" onClick={addOption} disabled={disabled}>
              <i className="ph ph-plus me-1" /> Add Option
            </button>
          )}
        </div>
      )}

      {['short_text', 'fill_blank'].includes(formData.question_type) && (
        <>
          <div className="col-12">
            <label className="h5 mb-8 fw-semibold font-heading d-block">Accepted Answers <span className="text-red-500">*</span></label>
            {acceptedAnswers.map((a, idx) => (
              <div key={idx} className="d-flex align-items-center gap-8 mb-8">
                <input type="text" className="form-control py-9" placeholder={`Accepted answer ${idx + 1}`}
                  value={a} onChange={(e) => updateAcceptedAnswer(idx, e.target.value)} disabled={disabled} />
                {acceptedAnswers.length > 1 && (
                  <button type="button" className="btn btn-sm py-4 px-8" style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 }} onClick={() => removeAcceptedAnswer(idx)} disabled={disabled}>
                    <i className="ph ph-x" />
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-sm btn-outline-main py-6 px-12 mt-4" onClick={addAcceptedAnswer} disabled={disabled}>
              <i className="ph ph-plus me-1" /> Add Accepted Answer
            </button>
          </div>
          <div className="col-12">
            <label className="d-flex align-items-center gap-8">
              <input type="checkbox" checked={!!formData.case_sensitive} onChange={(e) => set('case_sensitive', e.target.checked)} disabled={disabled} />
              <span>Case-sensitive matching</span>
            </label>
          </div>
        </>
      )}

      {formData.question_type === 'numerical' && (
        <>
          <div className="col-sm-6">
            <label className="h5 mb-8 fw-semibold font-heading">Correct Value <span className="text-red-500">*</span></label>
            <input type="number" step="any" className="form-control py-11" value={formData.numerical_correct_value ?? ''}
              onChange={(e) => set('numerical_correct_value', e.target.value)} disabled={disabled} />
          </div>
          <div className="col-sm-6">
            <label className="h5 mb-8 fw-semibold font-heading">Tolerance (±)</label>
            <input type="number" step="any" min="0" className="form-control py-11" value={formData.numerical_tolerance ?? 0}
              onChange={(e) => set('numerical_tolerance', e.target.value)} disabled={disabled} />
          </div>
        </>
      )}

      {formData.question_type === 'long_answer' && (
        <div className="col-12">
          <div className="alert alert-info mb-0">
            <i className="ph ph-info me-2" />
            This question type is never auto-scored. Student answers are marked "Pending Evaluation" and an admin
            must manually award marks after submission.
          </div>
        </div>
      )}

      <div className="col-sm-6">
        <label className="h5 mb-8 fw-semibold font-heading">Difficulty Level</label>
        <select className="form-control py-11" value={formData.difficulty_level || ''} onChange={(e) => set('difficulty_level', e.target.value)} disabled={disabled}>
          <option value="">Not set</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>

      <div className="col-12">
        <label className="h5 mb-8 fw-semibold font-heading d-block">Explanation (shown on result when allowed)</label>
        <textarea className="form-control py-11" rows={2} value={formData.explanation || ''} onChange={(e) => set('explanation', e.target.value)} disabled={disabled} />
      </div>
    </div>
  );
}

export default MockExamQuestionForm;
export { QUESTION_TYPES };
